import { useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { Download, Mail, Loader2, X, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Report-level share actions: Download as PDF + Email as PDF attachment.
 * `resultId` is the service_result document id.
 */
export default function ReportActions({ resultId, className = "" }) {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [target, setTarget] = useState(user?.email || "");
  const [sending, setSending] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/services/result/${resultId}/pdf`, { responseType: "blob" });
      const disp = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(disp);
      const filename = m ? m[1] : `aqion-rapor-${resultId}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDownloading(false); }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!target.trim()) return;
    setSending(true);
    try {
      await api.post(`/services/result/${resultId}/email`, { to: target.trim() });
      toast.success(`Rapor ${target} adresine gönderildi`);
      setShowEmail(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="report-actions">
      <button onClick={download} disabled={downloading} className="aq-button-ghost !py-2 !px-3 !text-[0.65rem]" data-testid="report-pdf-btn">
        {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        PDF olarak indir
      </button>
      <button onClick={() => setShowEmail((v) => !v)} className="aq-button-ghost !py-2 !px-3 !text-[0.65rem]" data-testid="report-email-btn">
        <Mail size={13} /> E-posta ile gönder
      </button>

      {showEmail && (
        <form onSubmit={sendEmail} className="w-full mt-3 flex flex-wrap items-end gap-3 p-4 rounded-xl border border-[#B6B09F]/60 bg-[#EAE4D5]/40" data-testid="report-email-form">
          <div className="flex-1 min-w-[220px]">
            <label className="label-eyebrow block mb-2">Alıcı e-posta</label>
            <input
              type="email"
              required
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="ornek@sirket.com"
              className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-2"
              data-testid="report-email-input"
            />
          </div>
          <button type="submit" disabled={sending} className="aq-button-primary !py-2 !px-4 !text-[0.68rem]" data-testid="report-email-send">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Gönder
          </button>
          <button type="button" onClick={() => setShowEmail(false)} className="aq-button-ghost !py-2 !px-3 !text-[0.65rem]" data-testid="report-email-close">
            <X size={13} /> Kapat
          </button>
        </form>
      )}
    </div>
  );
}

/** Unused but kept for clarity — direct browser URL when we want to open in new tab. */
export const pdfUrlFor = (id) => `${API}/services/result/${id}/pdf`;
